import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../components/layout/Footer';
import authService from '../services/authService';

const postVerificationLoginState = { from: '/profile' };

const VerifyEmail = () => {
    const { t } = useTranslation('auth');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setError(t('verifyEmail.invalidToken'));
            setLoading(false);
            return;
        }

        const verify = async () => {
            try {
                await authService.verifyEmail(token);
                setMessage(t('verifyEmail.successMessage'));
                setTimeout(() => {
                    navigate('/login', { replace: true, state: postVerificationLoginState });
                }, 3000);
            } catch (err) {
                setError(err.message || t('verifyEmail.expiredToken'));
            } finally {
                setLoading(false);
            }
        };

        verify();
    }, [token, t, navigate]);

    if (!token) {
        return (
            <div className="bg-white min-h-screen font-montserrat">
                <section className="relative w-full h-[200px] sm:h-[250px] md:h-[300px] flex items-center justify-center text-white text-center overflow-hidden mb-0 px-4 sm:px-6 lg:px-8" style={{ margin: '10px auto' }}>
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-800 z-0" />
                    <div className="relative z-20 flex flex-col items-center justify-center w-full h-full">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal mb-3 sm:mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('verifyEmail.invalidLinkTitle')}</h1>
                        <p className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto opacity-90 px-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('verifyEmail.invalidLinkSubtitle')}</p>
                    </div>
                </section>
                <div className="w-full border-t border-gray-200 my-0" />
                <section className="py-8 sm:py-12 lg:py-16 max-w-md mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-gray-700 mb-6">{t('verifyEmail.invalidLinkMessage')}</p>
                    <Link to="/verify-email-pending" className="text-black hover:underline" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {t('verifyEmail.requestNewLink')}
                    </Link>
                </section>
                <Footer />
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen font-montserrat">
            <section className="relative w-full h-[200px] sm:h-[250px] md:h-[300px] flex items-center justify-center text-white text-center overflow-hidden mb-0 px-4 sm:px-6 lg:px-8" style={{ margin: '10px auto' }}>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-800 z-0" />
                <div className="relative z-20 flex flex-col items-center justify-center w-full h-full">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal mb-3 sm:mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('verifyEmail.heroTitle')}</h1>
                    <p className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto opacity-90 px-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('verifyEmail.heroSubtitle')}</p>
                </div>
            </section>

            <div className="w-full border-t border-gray-200 my-0" />

            <section className="py-8 sm:py-12 lg:py-16 max-w-md mx-auto px-4 sm:px-6 lg:px-8 text-center">
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
                        {error}
                        <div className="mt-4">
                            <Link to="/verify-email-pending" className="text-black hover:underline" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                {t('verifyEmail.requestNewLink')}
                            </Link>
                        </div>
                    </div>
                )}

                {message && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded">
                        {message}
                        <p className="mt-2 text-sm">{t('verifyEmail.redirectNote')}</p>
                    </div>
                )}

                {loading && !error && !message && (
                    <div>
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-gray-700">{t('verifyEmail.verifying')}</p>
                    </div>
                )}

                {message && (
                    <Link to="/login" state={postVerificationLoginState} className="text-black hover:underline" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {t('verifyEmail.goToLogin')}
                    </Link>
                )}
            </section>

            <Footer />
        </div>
    );
};

export default VerifyEmail;
